const EARTH_RADIUS_METERS = 6378137;

export interface YardGeoref {
    H: number[][];
    origin: {
        lat: number;
        lon: number;
    };
}

export function gpsToYardPixel(
    lat: number,
    lon: number,
    georef: YardGeoref
): [number, number] {
    const { H, origin } = georef;

    if (
        H.length !== 3 ||
        H.some(row => row.length !== 3) ||
        H.flat().some(value => !Number.isFinite(value)) ||
        ![lat, lon, origin.lat, origin.lon].every(Number.isFinite)
    ) {
        throw new Error("Invalid GPS calibration data.");
    }

    const radians = Math.PI / 180;

    const east =
        (lon - origin.lon) *
        radians *
        EARTH_RADIUS_METERS *
        Math.cos(origin.lat * radians);

    const north =
        (lat - origin.lat) *
        radians *
        EARTH_RADIUS_METERS;

    // H converts image pixels to local coordinates.
    // Solve the inverse projective mapping directly.
    const a = H[0][0] - east * H[2][0];
    const b = H[0][1] - east * H[2][1];
    const c = east * H[2][2] - H[0][2];

    const d = H[1][0] - north * H[2][0];
    const e = H[1][1] - north * H[2][1];
    const f = north * H[2][2] - H[1][2];

    const determinant = a * e - b * d;

    if (
        !Number.isFinite(determinant) ||
        Math.abs(determinant) < 1e-12
    ) {
        throw new Error("GPS projection cannot be inverted.");
    }

    const x = (c * e - b * f) / determinant;
    const y = (a * f - c * d) / determinant;

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        throw new Error("GPS waypoint cannot be mapped to the yard image.");
    }

    return [x, y];
}
